import React from 'react';
import {
  Sparkles,
  ShieldCheck,
  Clock,
  Heart,
  Camera,
  GitBranch,
  Users,
  ArrowRight,
  Image as ImageIcon,
  BookOpen,
  Link2,
  Lock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * ConversionEnhancements — additive presentational pieces layered on top
 * of the public invite/join surfaces to lift conversions and create
 * urgency. Each piece is exported individually so the host pages can
 * place them at exactly the right scroll-depth without changing their
 * existing layout, hero, or Stripe wiring.
 *
 * Pieces:
 *   - TopConversionBar          → very top of the page, invite context
 *   - QuickTrialCTA             → directly under hero, above the fold
 *   - EmotionalHookSection      → "Don't lose your family's story"
 *   - ViralHookSection          → "Build something your whole family will use"
 *   - StickyMobileTrialCTA      → fixed-bottom mobile-only Start Free Trial
 *
 * NONE of these touch invite, auth, Stripe, family-graph, or routing
 * logic — they only render markup and forward click events to the
 * onCta callback the host page supplies (which is already wired to the
 * existing openMembershipModal / openAuthModal flow).
 */

/* ─────────────────────── 1. Top conversion bar ─────────────────────── */
export const TopConversionBar: React.FC<{
  inviterName?: string | null;
  onCta?: () => void;
}> = ({ inviterName, onCta }) => {
  const firstName = inviterName ? inviterName.split(' ')[0] : null;
  return (
    <div className="bg-gradient-to-r from-fuchsia-600 via-pink-500 to-amber-400 text-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-2.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-center sm:text-left">
        <div className="flex items-center justify-center sm:justify-start gap-2 text-sm font-medium">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/20 backdrop-blur text-[10px] font-extrabold uppercase tracking-widest">
            <Sparkles className="w-3 h-3" /> Invited
          </span>
          <span className="leading-snug">
            {firstName ? (
              <>
                <span className="font-bold">{firstName} invited you</span>
                <span className="hidden sm:inline">
                  {' '}
                  to join a private family on KinsCircle
                </span>
              </>
            ) : (
              <>You've been invited to join a private family on KinsCircle</>
            )}
          </span>
        </div>
        {onCta && (
          <button
            onClick={onCta}
            className="inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-full bg-white text-fuchsia-700 text-xs font-bold shadow hover:bg-fuchsia-50 transition"
          >
            Accept invite <ArrowRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────── 2. Quick trial CTA ─────────────────────── */
export const QuickTrialCTA: React.FC<{
  onCta: () => void;
  label?: string;
}> = ({ onCta, label = 'Start Free Trial' }) => {
  return (
    <section className="rounded-3xl bg-white border-2 border-fuchsia-200 shadow-xl overflow-hidden">
      <div className="p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
        <div className="flex-1 text-center sm:text-left">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-extrabold uppercase tracking-widest mb-2">
            <Clock className="w-3 h-3" /> Limited time
          </div>
          <h3 className="font-serif text-xl sm:text-2xl font-bold text-slate-900 leading-tight">
            Start your family in 60 seconds
          </h3>
          <p className="mt-1 text-sm text-slate-600">
            <span className="font-semibold text-slate-800">3-Day Free Trial</span> · Card
            Required · Cancel Anytime. <span className="text-slate-700">No charge today</span> —
            full access from day one.
          </p>
          <p className="mt-1.5 text-[11px] text-slate-700 font-semibold">
            <span className="font-extrabold">$4.99/month</span> after trial · cancel before
            trial ends to avoid charges
          </p>
          <p className="mt-0.5 text-[11px] text-amber-700 font-semibold">
            Or save with yearly: <span className="font-extrabold">$14.99/year</span>
            <span className="ml-1 line-through text-slate-400 font-normal">$24.99/yr</span>
            <span className="ml-1">— Founder's Discount, best value</span>
          </p>


        </div>
        <Button
          onClick={onCta}
          className="h-12 sm:h-14 text-base bg-gradient-to-r from-fuchsia-600 via-pink-500 to-amber-400 hover:opacity-95 text-white font-semibold gap-2 shadow-xl rounded-2xl px-6 sm:px-8 w-full sm:w-auto flex-shrink-0"
        >
          <Sparkles className="w-5 h-5" />
          {label}
          <ArrowRight className="w-5 h-5 ml-1" />
        </Button>
      </div>
    </section>
  );
};

/* ─────────────────────── 3. Emotional hook ─────────────────────── */
export const EmotionalHookSection: React.FC = () => {
  const losses: Array<{ icon: React.ReactNode; title: string; body: string }> = [
    {
      icon: <ImageIcon className="w-5 h-5 text-rose-600" />,
      title: 'Photos get lost',
      body: 'Phones break, hard drives fail, and decades of family photos vanish overnight.',
    },
    {
      icon: <BookOpen className="w-5 h-5 text-rose-600" />,
      title: 'Stories get forgotten',
      body: "Grandparents' stories fade if no one writes them down — and no one ever does.",
    },
    {
      icon: <Link2 className="w-5 h-5 text-rose-600" />,
      title: 'Generations drift apart',
      body: "Cousins grow up not knowing each other. Branches of the family quietly disappear.",
    },
  ];
  return (
    <section className="rounded-3xl bg-gradient-to-br from-rose-50 via-pink-50 to-white border border-rose-100 p-6 sm:p-10">
      <div className="text-center max-w-2xl mx-auto">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-100 text-rose-700 text-[11px] font-bold uppercase tracking-widest mb-3">
          <Heart className="w-3.5 h-3.5 fill-rose-500" /> Why it matters
        </div>
        <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">
          Don't lose your family's story
        </h2>
      </div>
      <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-3">
        {losses.map((l) => (
          <div
            key={l.title}
            className="rounded-2xl bg-white border border-rose-100 p-4 shadow-sm"
          >
            <div className="inline-flex w-10 h-10 rounded-full bg-rose-50 items-center justify-center border border-rose-100 mb-2">
              {l.icon}
            </div>
            <div className="font-bold text-sm text-slate-900">{l.title}</div>
            <div className="text-xs text-slate-600 mt-1 leading-snug">{l.body}</div>
          </div>
        ))}
      </div>
      <div className="mt-7 text-center">
        <p className="font-serif text-lg sm:text-xl text-slate-900 font-semibold">
          KinsCircle keeps everything together{' '}
          <span className="bg-gradient-to-r from-fuchsia-600 to-pink-500 bg-clip-text text-transparent">
            forever
          </span>
          .
        </p>
      </div>
    </section>
  );
};

/* ─────────────────────── 4. Viral hook ─────────────────────── */
export const ViralHookSection: React.FC = () => {
  const items: Array<{ icon: React.ReactNode; title: string; body: string }> = [
    {
      icon: <Users className="w-5 h-5 text-fuchsia-600" />,
      title: 'Everyone can join',
      body: 'Parents, grandparents, cousins, kids — invite the whole family in seconds.',
    },
    {
      icon: <Camera className="w-5 h-5 text-pink-600" />,
      title: 'Everyone can add memories',
      body: "Photos, videos and stories from every branch — collected in one shared place.",
    },
    {
      icon: <GitBranch className="w-5 h-5 text-amber-600" />,
      title: 'Everyone can grow the tree',
      body: 'Each new relative pulls their branch in too. Your tree grows itself.',
    },
  ];
  return (
    <section className="rounded-3xl bg-white border border-fuchsia-100 p-6 sm:p-10 shadow-sm">
      <div className="text-center max-w-2xl mx-auto">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-fuchsia-100 text-fuchsia-700 text-[11px] font-bold uppercase tracking-widest mb-3">
          <Sparkles className="w-3.5 h-3.5" /> Built for families
        </div>
        <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">
          Build something your whole family will use
        </h2>
      </div>
      <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-3">
        {items.map((it) => (
          <div
            key={it.title}
            className="rounded-2xl bg-gradient-to-br from-fuchsia-50 via-pink-50 to-amber-50 border border-fuchsia-100 p-4"
          >
            <div className="inline-flex w-10 h-10 rounded-full bg-white items-center justify-center shadow-sm border border-fuchsia-100 mb-2">
              {it.icon}
            </div>
            <div className="font-bold text-sm text-slate-900">{it.title}</div>
            <div className="text-xs text-slate-600 mt-1 leading-snug">{it.body}</div>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ─────────────────────── 5. Sticky mobile CTA ─────────────────────── */
export const StickyMobileTrialCTA: React.FC<{
  onCta: () => void;
  label?: string;
}> = ({ onCta, label = 'Start Free Trial' }) => {
  return (
    <>
      {/* Spacer so page content isn't hidden behind the fixed bar on mobile */}
      <div className="h-20 sm:hidden" aria-hidden />
      <div
        className="sm:hidden fixed bottom-0 inset-x-0 z-40 px-3 pb-3 pt-2 bg-gradient-to-t from-white via-white/95 to-white/0"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}
      >
        <Button
          onClick={onCta}
          className="w-full h-13 py-3.5 text-base bg-gradient-to-r from-fuchsia-600 via-pink-500 to-amber-400 hover:opacity-95 text-white font-semibold gap-2 shadow-2xl rounded-2xl"
        >
          <Sparkles className="w-5 h-5" />
          {label}
          <ArrowRight className="w-5 h-5 ml-1" />
        </Button>
        <div className="mt-1.5 text-center text-[10px] text-slate-500 flex items-center justify-center gap-2">
          <Lock className="w-3 h-3" />
          3-Day Free Trial · Card Required · Cancel Anytime
        </div>
      </div>
    </>
  );
};


/* ─────────────────────── Bonus: trust mini-strip ─────────────────────── */
export const TrustMiniStrip: React.FC = () => (
  <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[12px] text-slate-600">
    <span className="inline-flex items-center gap-1.5">
      <ShieldCheck className="w-4 h-4 text-emerald-600" />
      Private &amp; secure
    </span>
    <span className="inline-flex items-center gap-1.5">
      <Lock className="w-4 h-4 text-emerald-600" />
      Family-only access
    </span>
    <span className="inline-flex items-center gap-1.5">
      <Heart className="w-4 h-4 text-pink-500 fill-pink-500" />
      No ads, ever
    </span>
  </div>
);

export default {
  TopConversionBar,
  QuickTrialCTA,
  EmotionalHookSection,
  ViralHookSection,
  StickyMobileTrialCTA,
  TrustMiniStrip,
};
